'use client';

// One small UI preference kept in localStorage, readable by every component in
// the tab and kept in sync across tabs. Lifted out of `useDockedLotPanel`
// (BL-2342) so the offline-mode flag and the docked-panel choice share one copy,
// and moved here (BL-2588) so bidder-web shares it for the sound switch.
// For UI state only (rule #16): never a cache of server state.

import { useCallback, useState, useSyncExternalStore } from 'react';

// The `storage` event only fires in OTHER tabs, so same-tab writers notify
// their readers through this set.
const listeners = new Set<() => void>();

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  window.addEventListener('storage', onChange);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener('storage', onChange);
  };
}

function read(key: string | null): string | null {
  // No key yet (e.g. no tenant/user): like blocked storage, nothing stored.
  if (!key) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // storage blocked: the caller's default
  }
}

/** Persists the value (null removes it); returns false when storage is blocked. */
function write(key: string | null, value: string | null): boolean {
  if (!key) return false;
  try {
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    return false;
  }
  listeners.forEach((l) => l());
  return true;
}

/** What {@link useStoredPreference} returns. */
export interface StoredPreference {
  /**
   * The stored string, `null` when nothing is stored, or `undefined` until it
   * can be read (the server render and the first paint), so a caller never
   * flashes the default for a user who chose otherwise.
   */
  value: string | null | undefined;
  /** Store a value, or remove it with `null`. */
  setValue: (next: string | null) => void;
}

/**
 * Read and write one localStorage preference.
 *
 * Blocked storage (a private window, blocked site data) keeps the choice for
 * this view only, so the control still responds.
 *
 * @param key - the storage key, or null while it can't be formed yet.
 * @returns the value and its setter ({@link StoredPreference}).
 * @sideEffect `setValue` writes localStorage and notifies every reader in the tab.
 */
export function useStoredPreference(key: string | null): StoredPreference {
  const stored = useSyncExternalStore<string | null | undefined>(
    subscribe,
    () => read(key),
    () => undefined,
  );
  const [unsaved, setUnsaved] = useState<{ key: string | null; value: string | null } | null>(null);
  const value = unsaved?.key === key ? unsaved.value : stored;
  const setValue = useCallback(
    (next: string | null) => {
      if (write(key, next)) setUnsaved(null);
      else setUnsaved({ key, value: next });
    },
    [key],
  );
  return { value, setValue };
}
